import { create } from "zustand";
import { persist } from "zustand/middleware";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      currentOutlet: null,
      currentProperty: null,

      setAuth: (user, token) => {
        set({ user, token, isAuthenticated: true });
      },

      setCurrentOutlet: (outlet) => {
        set({ currentOutlet: outlet });
      },

      setCurrentProperty: (property) => {
        set({ currentProperty: property });
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, currentOutlet: null, currentProperty: null });
        localStorage.removeItem("auth-storage");
      },

      // Support access - allows super admin to access tenant admin panel
      setSupportAccess: (user, token, tenant, property) => {
        set({ 
          user: { ...user, is_support_session: true },
          token, 
          isAuthenticated: true,
          currentProperty: property,
          currentOutlet: null
        });
      },

      // Exit support mode and return to super admin
      exitSupportMode: () => {
        // Clear current session and require fresh login
        set({ user: null, token: null, isAuthenticated: false, currentOutlet: null, currentProperty: null });
        localStorage.removeItem("auth-storage");
      },

      getAuthHeader: () => {
        const token = get().token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
    {
      name: "auth-storage",
    }
  )
);

// Cart Store for POS
export const useCartStore = create((set, get) => ({
  items: [],
  customerName: "",
  customerPhone: "",
  orderType: "retail",
  taxCalculationMethod: "exclusive",  // "exclusive" or "inclusive"
  taxRate: 16,  // Default 16% VAT

  addItem: (product) => {
    const items = get().items;
    // For customized items, use customization_key to identify unique items
    // Otherwise, use product_id
    const itemKey = product.customization_key || product.product_id || product.id;
    const existingIndex = items.findIndex((item) => {
      if (product.customization_key) {
        return item.customization_key === product.customization_key;
      }
      // For non-customized items, only match if target also has no customizations
      return item.product_id === (product.product_id || product.id) && !item.customization_key;
    });

    if (existingIndex >= 0) {
      const newItems = [...items];
      newItems[existingIndex].quantity += 1;
      set({ items: newItems });
    } else {
      set({
        items: [
          ...items,
          {
            product_id: product.product_id || product.id,
            name: product.name,
            price: product.price,
            quantity: 1,
            image_url: product.image_url,
            product_type: product.product_type || "food",
            // Retail variant data
            variant_id: product.variant_id || null,
            variant_label: product.variant_label || "",
            sku: product.sku || "",
            // Customization data
            prep_instructions: product.prep_instructions || "",
            prep_options_selected: product.prep_options_selected || [],
            selected_options: product.selected_options || [],
            options_total: product.options_total || 0,
            // Meal/Combo data
            meal_selections: product.meal_selections || [],
            is_meal: product.is_meal || false,
            customization_key: product.customization_key || null,
          },
        ],
      });
    }
  },

  removeItem: (key) => {
    set({ items: get().items.filter((item) => (item.customization_key || item.product_id) !== key) });
  },

  updateQuantity: (key, quantity) => {
    if (quantity <= 0) {
      get().removeItem(key);
      return;
    }
    const items = get().items.map((item) =>
      (item.customization_key || item.product_id) === key ? { ...item, quantity } : item
    );
    set({ items });
  },

  setCustomerInfo: (name, phone) => {
    set({ customerName: name, customerPhone: phone });
  },

  setOrderType: (type) => {
    set({ orderType: type });
  },

  setTaxMethod: (method) => {
    set({ taxCalculationMethod: method });
  },

  setTaxRate: (rate) => {
    set({ taxRate: rate });
  },

  getSubtotal: () => {
    const items = get().items;
    const grossTotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const taxMethod = get().taxCalculationMethod || 'exclusive';
    const taxRate = get().taxRate || 16;
    
    if (taxMethod === 'inclusive') {
      // For inclusive tax, subtotal is the net amount (before tax)
      return grossTotal / (1 + taxRate / 100);
    }
    return grossTotal;
  },

  getTax: () => {
    const items = get().items;
    const grossTotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const taxMethod = get().taxCalculationMethod || 'exclusive';
    const taxRate = get().taxRate || 16;
    
    if (taxMethod === 'inclusive') {
      // For inclusive, extract tax from gross total
      return grossTotal - (grossTotal / (1 + taxRate / 100));
    }
    // For exclusive, add tax on top
    return grossTotal * (taxRate / 100);
  },

  getTotal: () => {
    const items = get().items;
    const grossTotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const taxMethod = get().taxCalculationMethod || 'exclusive';
    const taxRate = get().taxRate || 16;
    
    if (taxMethod === 'inclusive') {
      // For inclusive, total is the gross amount (prices already include tax)
      return grossTotal;
    }
    // For exclusive, total = subtotal + tax
    return grossTotal * (1 + taxRate / 100);
  },

  clearCart: () => {
    set({ items: [], customerName: "", customerPhone: "", orderType: "retail" });
  },
}));

// Theme Store
export const useThemeStore = create(
  persist(
    (set) => ({
      theme: "light",
      setTheme: (theme) => {
        set({ theme });
        if (theme === "dark") {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      },
      toggleTheme: () => {
        set((state) => {
          const newTheme = state.theme === "dark" ? "light" : "dark";
          if (newTheme === "dark") {
            document.documentElement.classList.add("dark");
          } else {
            document.documentElement.classList.remove("dark");
          }
          return { theme: newTheme };
        });
      },
    }),
    {
      name: "theme-storage",
    }
  )
);

// API Helper
// Helper to make XHR request (bypass potential fetch interceptors)
const makeXhrRequest = (method, url, data, headers) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    
    // Set headers
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    
    xhr.onload = function() {
      let responseData = null;
      try {
        responseData = JSON.parse(xhr.responseText);
      } catch {
        responseData = { rawText: xhr.responseText };
      }
      
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        data: responseData
      });
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(data);
  });
};

export const api = {
  async request(endpoint, options = {}) {
    const { getAuthHeader } = useAuthStore.getState();
    
    // Don't set Content-Type for FormData - let browser set it with boundary
    const isFormData = options.body instanceof FormData;
    
    const headers = {
      ...(!isFormData && { "Content-Type": "application/json" }),
      ...getAuthHeader(),
      ...options.headers,
    };
    
    // Remove Content-Type header if it's FormData (browser will set it)
    if (isFormData && headers["Content-Type"]) {
      delete headers["Content-Type"];
    }

    const method = options.method || 'GET';
    const url = `${API_URL}${endpoint}`;
    const body = options.body || null;
    
    let result;
    try {
      result = await makeXhrRequest(method, url, body, headers);
    } catch (error) {
      throw new Error(`Network error: ${error.message}`);
    }

    // Handle error responses
    if (!result.ok) {
      let errorDetail = result.data?.detail || result.data?.message || result.data?.error || 
                         `Request failed (${result.status})`;
      // Handle Pydantic validation errors (detail is an array of objects)
      if (Array.isArray(errorDetail)) {
        errorDetail = errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
      } else if (typeof errorDetail === 'object') {
        errorDetail = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      throw new Error(errorDetail);
    }

    return result.data;
  },

  get: (endpoint) => api.request(endpoint),
  post: (endpoint, data, options = {}) => {
    // Handle FormData differently - don't stringify
    if (data instanceof FormData) {
      return api.request(endpoint, { method: "POST", body: data, ...options });
    }
    return api.request(endpoint, { method: "POST", body: JSON.stringify(data), ...options });
  },
  put: (endpoint, data) => api.request(endpoint, { method: "PUT", body: JSON.stringify(data) }),
  patch: (endpoint, data) => api.request(endpoint, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (endpoint) => api.request(endpoint, { method: "DELETE" }),
};
